import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import BulbIcon from '../components/common/icons/BulbIcon';
import LogoIcon from '../components/common/icons/LogoIcon';
import { useAuth } from '../hooks/useAuth';
import { consumeReturnUrl } from '../utils/returnUrl';

const PageContainer = styled.div`
  min-height: 100vh;
  min-width: 100vw;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, #2B7A9B 0%, #8B3B8B 100%);
  position: relative;
  overflow: hidden;
`;

const Logo = styled.div`
  position: absolute;
  top: 20px;
  left: auto;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 20px;
`;

const WelcomeText = styled.h1`
  font-size: 160px;
  color: white;
  font-weight: 400;
  margin: 0;
  line-height: 121%;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
`;

const BrandContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const BrandIcon = styled.div`
  width: 62.5px;
  height: 60px;
  background-color: #00E5FF;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 20px rgba(0, 229, 255, 0.3);
`;

const BrandText = styled.div`
  font-size: 78px;
  color: white;
  line-height: 121%;
  font-weight: 400;
  display: flex;
  align-items: center;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;

  span:first-child {
    font-weight: 400;
  }

  span:last-child {
    font-weight: 600;
    background: linear-gradient(90deg, #0CF3FF 0%, #07EBC9 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const LoadingText = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: 16px;
  margin-top: 8px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
`;

const LoadingSpinner = styled.div`
  width: 24px;
  height: 24px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #00E5FF;
  animation: spin 1s linear infinite;
  margin-top: 200px;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ErrorIcon = () => (
  <svg width="70" height="70" viewBox="0 0 70 70" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M28.9378 10.5C31.6321 5.83332 38.3679 5.83333 41.0622 10.5L59.2487 42C61.943 46.6667 58.5751 52.5 53.1865 52.5H16.8135C11.4249 52.5 8.05699 46.6667 10.7513 42L28.9378 10.5Z" fill="#FB155F" />
    <path d="M35 19C36.423 19 37.5452 20.2108 37.4373 21.6298L36.5251 33.6203C36.4645 34.4177 35.7997 35.0338 35 35.0338C34.2003 35.0338 33.5355 34.4177 33.4749 33.6203L32.5627 21.6298C32.4548 20.2108 33.577 19 35 19ZM32 40.8003C32 39.933 32.2857 39.1946 32.8571 38.5851C33.4505 37.9522 34.1648 37.6357 35 37.6357C35.8352 37.6357 36.5385 37.9522 37.1099 38.5851C37.7033 39.1946 38 39.933 38 40.8003C38 41.6676 37.7033 42.4177 37.1099 43.0506C36.5385 43.6835 35.8352 44 35 44C34.1868 44 33.4835 43.6835 32.8901 43.0506C32.2967 42.4177 32 41.6676 32 40.8003Z" fill="white" />
  </svg>
);

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  color: white;
  margin-top: 0px;
`;

const ErrorMessage = styled.h2`
  color: white;
  font-size: 20px;
  font-weight: 500;
  margin: 0;
`;

const LoginButton = styled.button`
  background: linear-gradient(90deg, #0CF3FF 0%, #07EBC9 100%);
  border: none;
  border-radius: 8px;
  color: #702DFF;
  font-size: 18px;
  font-weight: 600;
  padding: 16px 32px;
  cursor: pointer;
  margin-top: 40px;
  transition: all 0.3s ease;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  
  &:active {
    transform: translateY(0);
  }
`;

const Login: React.FC = () => {
  const { account, login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isLogoutFlag = searchParams.get('logout') === '1' || searchParams.get('logout') === 'true';
  const [authError, setAuthError] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [hasAttemptedSilentLogin, setHasAttemptedSilentLogin] = useState(false);

  const attemptAutoLogin = useCallback(async () => {
    setHasAttemptedSilentLogin(true);
    setIsLoggingIn(true);
    setAuthError(false);

    try {
      await login();
      navigate(consumeReturnUrl() ?? '/dashboard', { replace: true });
    } catch (error) {
      setAuthError(true);
      setIsLoggingIn(false);
      console.error('Login error:', error);
    }
  }, [login, navigate]);

  useEffect(() => {
    if (account) {
      if (!isLogoutFlag) {
        navigate(consumeReturnUrl() ?? '/dashboard', { replace: true });
      }
      return;
    }
    if (!hasAttemptedSilentLogin) {
      if (isLogoutFlag) {
        setHasAttemptedSilentLogin(true);
        setIsLoggingIn(false);
      } else {
        attemptAutoLogin();
      }
    }
  }, [account, navigate, hasAttemptedSilentLogin, isLogoutFlag, attemptAutoLogin]);


  const handleManualLogin = async () => {
    setIsLoggingIn(true);
    setAuthError(false);

    try {
      await login();
      navigate(consumeReturnUrl() ?? '/dashboard', { replace: true });
    } catch (error) {
      setAuthError(true);
      setIsLoggingIn(false);
      console.error('Login error:', error);
    }
  };


  return (
    <PageContainer>
      <Logo>
        <LogoIcon />
      </Logo>
      <ContentContainer>
        <WelcomeText>Welcome to</WelcomeText>
        <BrandContainer>
          <BrandIcon>
            <BulbIcon />
          </BrandIcon>
          <BrandText>
            <span>Eyepax</span>
            <span>Insight</span>
          </BrandText>
        </BrandContainer>
        {authError && (
          <ErrorContainer>
            <ErrorIcon />
            <ErrorMessage style={{ marginBottom: '-20px' }}>Sorry, you don't have access to this portal.</ErrorMessage>
            <LoginButton onClick={handleManualLogin}>
              <svg width="20" height="20" viewBox="0 0 31 31" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0 0H14.7618V14.7618H0V0Z" fill="#F35325"/>
                <path d="M16.2383 0H31.0001V14.7618H16.2383V0Z" fill="#81BC06"/>
                <path d="M0 16.238H14.7618V30.9998H0V16.238Z" fill="#05A6F0"/>
                <path d="M16.2383 16.238H31.0001V30.9998H16.2383V16.238Z" fill="#FFBA08"/>
              </svg>
              Sign in with Microsoft
            </LoginButton>
          </ErrorContainer>
        )}
        {!authError && isLogoutFlag && !isLoggingIn && hasAttemptedSilentLogin && (
          <>
            <ErrorMessage style={{ marginTop: '50px', marginBottom: '-30px' }}>You’ve successfully signed out. Please sign in again to continue.</ErrorMessage>
            <LoginButton onClick={handleManualLogin}>
              <svg width="20" height="20" viewBox="0 0 31 31" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0 0H14.7618V14.7618H0V0Z" fill="#F35325"/>
                <path d="M16.2383 0H31.0001V14.7618H16.2383V0Z" fill="#81BC06"/>
                <path d="M0 16.238H14.7618V30.9998H0V16.238Z" fill="#05A6F0"/>
                <path d="M16.2383 16.238H31.0001V30.9998H16.2383V16.238Z" fill="#FFBA08"/>
              </svg>
              Sign in with Microsoft
            </LoginButton>
          </>
        )}
        {isLoggingIn && (
          <>
            <LoadingSpinner />
            <LoadingText>We are verifying your credentials..</LoadingText>
          </>
        )}
      </ContentContainer>
    </PageContainer>
  );
};

export default Login; 